import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import koLocale from 'date-fns/locale/ko';

import { theme } from './styles/theme';
import { Layout } from './components/common';
import { useAuthStore } from './hooks/useAuth';

// Pages
import LoginPage from './views/auth/LoginPage';
import SignupPage from './views/auth/SignupPage';
import DashboardPage from './views/dashboard/DashboardPage';
import { ConsultationListPage } from './views/consultations';
import { ConsultationDocumentPage, DocumentListPage } from './views/documents';
import { ProfilePage } from './views/profile';
import { TemplateListPage, TemplateDetailPage } from './views/templates';
import { PromptListPage, PromptDetailPage } from './views/prompts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, fetchUser } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, fetchUser]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
          <CssBaseline />
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignupPage />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="consultations" element={<ConsultationListPage />} />
                {/* 상담일지 문서 작성 (통합 화면: 상담 기록 + 문서 작성) */}
                <Route path="consultations/new" element={<ConsultationDocumentPage />} />
                <Route path="consultations/:consultationId" element={<ConsultationDocumentPage />} />
                <Route path="consultations/:consultationId/edit" element={<ConsultationDocumentPage />} />
                <Route path="templates" element={<TemplateListPage />} />
                <Route path="templates/:id" element={<TemplateDetailPage />} />
                <Route path="templates/:id/edit" element={<TemplateDetailPage />} />
                <Route path="documents" element={<DocumentListPage />} />
                <Route path="prompts" element={<PromptListPage />} />
                <Route path="prompts/:id/edit" element={<PromptDetailPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings" element={<div>설정 (준비 중)</div>} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
